import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import Modal from '../../components/Modal.jsx';
import CourseThumbnail from '../../components/CourseThumbnail.jsx';
import { 
  PlusCircle, 
  Edit, 
  Trash2, 
  Eye, 
  MoreVertical,
  BookOpen,
  Clock,
  Users
} from 'lucide-react';
import { authContext } from '../../context/index.js';

const TeacherCourses = () => {
  const { backendUrl, userData } = useContext(authContext);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessonCounts, setLessonCounts] = useState({});

  // Fetch lesson counts for courses
  const fetchLessonCounts = async (courses) => {
    const counts = {};
    for (const course of courses) {
      try {
        const res = await axios.get(`${backendUrl}/api/lessions/${course._id}`, { withCredentials: true });
        counts[course._id] = res?.data?.count || 0;
      } catch (error) {
        counts[course._id] = 0;
      }
    }
    setLessonCounts(counts);
  };

  useEffect(() => {
    let mounted = true;
    const fetchCourses = async () => {
      try {
        setLoading(true);
        // Backend route is singular: /api/course
        const res = await axios.get(backendUrl + '/api/course', { withCredentials: true });
        if (!mounted) return;
        const list = res?.data?.courses || [];
        setAllCourses(list);
        
        // Fetch lesson counts after courses are loaded
        if (list.length > 0) {
          await fetchLessonCounts(list);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchCourses();
    return () => { mounted = false; };
  }, [backendUrl]);

  const myCourses = useMemo(() => {
    if (!userData?.email) return [];
    // getAllCourses populates 'creater' with name/email
    return allCourses.filter((c) => c?.creater?.email === userData.email);
  }, [allCourses, userData]);

  const filteredCourses = useMemo(() => {
    return myCourses.filter(course => {
      const matchesSearch = (course.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (course.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const statusStr = course.published ? 'published' : 'draft';
      const matchesFilter = filterStatus === 'all' || statusStr === filterStatus;
      return matchesSearch && matchesFilter;
    });
  }, [myCourses, searchQuery, filterStatus]);

  const handleDeleteCourse = (courseId) => {
    setCourseToDelete(courseId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    // Implement delete logic here (frontend only per instructions)
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  const getStatusColor = (published) => {
    return published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700';
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">My Courses</h1>
          <p className="text-secondary-600 mt-1">Manage and organize your course content</p>
        </div>
        <Link to="/teacher/courses/create">
          <Button className="flex items-center space-x-2">
            <PlusCircle size={20} />
            <span>Create Course</span>
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input w-full"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input w-40"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Scrollable Courses Area */}
      <div className="overflow-y-auto overscroll-contain pr-1 max-h-[calc(100vh-240px)]">
        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredCourses.map((course) => (
            <Card key={course._id} className="overflow-hidden course-card-hover flex flex-col" style={{ minHeight: '420px' }}>
              <div className="relative">
                <CourseThumbnail
                  src={course.thumbnail}
                  alt={course.title}
                  fallbackText="No thumbnail"
                />
                <div className="absolute top-2 left-2 z-10">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(course.published)}`}>
                    {course.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="absolute top-2 right-2 z-10">
                  <span className="bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium rounded-full text-secondary-700 shadow-sm">
                    <BookOpen size={12} className="inline mr-1" />
                    {lessonCounts[course._id] !== undefined ? lessonCounts[course._id] : '...'} lessons
                  </span>
                </div>
              </div>
              
              <div className="pt-4 px-4 pb-4 flex flex-col flex-1">
                <h3 
                  className="font-semibold text-secondary-900 mb-2 line-clamp-2 min-h-[3rem] cursor-pointer text-hover-primary transition-colors"
                  title={course.title}
                >
                  {course.title}
                </h3>
                <p 
                  className="text-sm text-secondary-600 mb-3 line-clamp-2 min-h-[2.5rem] cursor-pointer text-hover-secondary transition-colors"
                  title={course.description}
                >
                  {course.description}
                </p>
                
                <div className="flex items-center gap-3 text-xs text-secondary-600 mb-3">
                  <span className="flex items-center">
                    <Users size={14} className="mr-1" />
                    {course.studentEnroll?.length || 0} students
                  </span>
                  <span className="flex items-center">
                    <Clock size={14} className="mr-1" />
                    {course.createdAt ? new Date(course.createdAt).toLocaleDateString() : '--'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-lg font-bold text-primary-600">₹{course.price}</span>
                </div>
                
                <div className="flex justify-center items-center gap-2 mt-auto">
                  <Link to={`/teacher/courses/${course._id}/lessons`}>
                    <Button size="sm" className="rounded-md" variant="primary">
                      <PlusCircle size={16} className="mr-1" />
                      Add Lesson
                    </Button>
                  </Link>
                  <Link to={`/teacher/courses/${course._id}/edit`}>
                    <Button size="sm" className="rounded-md" variant="outline">
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                  </Link>
                  <Link to={`/teacher/courses/${course._id}/lessons`}>
                    <Button size="sm" className="rounded-md" variant="secondary">
                      <Eye size={16} className="mr-1" />
                      View
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {filteredCourses.length === 0 && (
          <Card className="text-center py-12">
            <BookOpen size={48} className="mx-auto text-secondary-400 mb-4" />
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">No courses found</h3>
            <p className="text-secondary-600 mb-6">
              {searchQuery || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by creating your first course'
              }
            </p>
            <Link to="/teacher/courses/create">
              <Button>
                <PlusCircle size={20} className="mr-2" />
                Create Your First Course
              </Button>
            </Link>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Course"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-secondary-600">
            Are you sure you want to delete this course? This action cannot be undone and will remove all associated data.
          </p>
          <div className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="secondary"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Course
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TeacherCourses;

